import { Activity, AlertTriangle, UserPlus, Clock, TrendingUp, ShieldAlert } from "lucide-react";
import { UnifiedRecord, SystemMetrics, AppView } from "../../../types";

interface DashboardProps {
  records: UnifiedRecord[];
  metrics: SystemMetrics | null;
  loading: boolean;
  onNavigate: (view: AppView, params?: any) => void;
}

export function DoctorDashboard({ records, loading }: DashboardProps) {
  // Compute JARVIS Metrics
  const myPatients = records.filter(r => r.his_clinical_status === "Admitted" || r.ward?.includes("ICU"));
  
  // High-priority patients
  const requireReview = myPatients.filter(r => 
    r.has_conflict || 
    r.lab_results.some(l => l.result_status !== "Completed" && l.priority === "STAT")
  );

  const pendingLabs = myPatients.reduce((sum, r) => sum + r.lab_results.filter(l => l.result_status !== "Completed").length, 0);

  // Since we don't have vitals, we use operational/lab anomalies as proxies for the demo
  const trendAlerts = requireReview.length; 

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 border-b border-indigo-500/20 pb-6 relative overflow-hidden animate-hud-fade" style={{ animationDelay: '0.1s', opacity: 0 }}>
        {/* Radar/Scan effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-500/10 to-transparent h-1/4 animate-scan pointer-events-none" />
        <div className="absolute -left-20 -top-20 w-64 h-64 bg-indigo-500/10 blur-[100px] rounded-full pointer-events-none" />
        
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-indigo-900/40 border border-indigo-500/30 flex items-center justify-center shadow-[0_0_20px_rgba(99,102,241,0.2)]">
            <Activity className="w-8 h-8 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-3xl font-light tracking-tight text-white flex items-center gap-2">
              Good morning, <span className="font-semibold bg-gradient-to-r from-indigo-400 to-teal-400 bg-clip-text text-transparent">Doctor</span>
            </h2>
            <p className="text-indigo-200/60 mt-1 flex items-center gap-2 font-mono text-xs uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-glow-pulse" />
              System Online // Clinical Command Center Active
            </p>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Patients Under Care", value: myPatients.length, icon: UserPlus, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30" },
          { label: "Require Review", value: requireReview.length, icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/50", glow: requireReview.length > 0 },
          { label: "Pending Investigations", value: pendingLabs, icon: Clock, color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/30" },
          { label: "Trend Alerts", value: trendAlerts, icon: TrendingUp, color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/30" }
        ].map((kpi, i) => (
          <div key={i} className={`rounded-2xl border ${kpi.border} bg-[#040d1a]/80 backdrop-blur-xl p-5 relative overflow-hidden group animate-hud-fade`} style={{ animationDelay: `${0.2 + i * 0.1}s`, opacity: 0 }}>
            {kpi.glow && <div className="absolute inset-0 bg-amber-500/5 animate-glow-pulse pointer-events-none" />}
            
            {/* Corner bracket accents */}
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-white/20 pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-white/20 pointer-events-none" />

            <div className="flex items-center gap-3 mb-3 relative z-10">
              <div className={`p-2 rounded-xl ${kpi.bg} ${kpi.color}`}>
                <kpi.icon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">{kpi.label}</span>
            </div>
            <div className={`text-4xl font-bold ${kpi.color} relative z-10 font-mono tracking-tighter`}>
              {loading ? "..." : kpi.value}
            </div>
          </div>
        ))}
      </div>

      {/* Priority Patients List */}
      <div className="rounded-3xl border border-indigo-500/20 bg-[#040d1a]/80 backdrop-blur-xl overflow-hidden relative">
        <div className="px-6 py-5 border-b border-indigo-500/10 flex items-center justify-between bg-indigo-500/5">
          <h3 className="text-lg font-medium text-indigo-100 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-indigo-400" />
            Prioritized Patients
          </h3>
          <span className="text-xs font-medium text-indigo-400 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20">
            {requireReview.length} Configured Risk Indicators
          </span>
        </div>

        <div className="p-6">
          {requireReview.length === 0 ? (
            <div className="text-center py-12">
              <ShieldAlert className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">All assigned patients are stable.</p>
              <p className="text-gray-500 text-sm mt-1">No configured review indicators triggered.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {requireReview.map((patient, idx) => {
                const pendingCount = patient.lab_results.filter(l => l.result_status !== "Completed").length;
                const statLabs = patient.lab_results.filter(l => l.priority === "STAT" && l.result_status !== "Completed");
                
                let flagReason = "";
                if (patient.has_conflict) flagReason = "Operational Desync / Conflict Detected";
                else if (statLabs.length > 0) flagReason = "STAT Investigation Pending";
                else flagReason = "Configured Indicator Changed";

                return (
                  <div key={idx} className="group relative rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 transition-all hover:bg-amber-500/10 hover:border-amber-500/50 hover:shadow-[0_0_15px_rgba(245,158,11,0.2)] animate-hud-fade" style={{ animationDelay: `${0.6 + idx * 0.15}s`, opacity: 0 }}>
                    {/* Decorative grid */}
                    <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#f59e0b_1px,transparent_1px),linear-gradient(to_bottom,#f59e0b_1px,transparent_1px)] bg-[size:2rem_2rem] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)', WebkitMaskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)' }} />

                    <div className="flex justify-between items-start mb-4 relative z-10">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 uppercase tracking-widest font-mono animate-glow-pulse">Review Recommended</span>
                        </div>
                        <h4 className="text-xl font-bold text-white group-hover:text-amber-300 transition-colors">
                          {patient.patient_name || patient.patient_id}
                        </h4>
                        <div className="text-[10px] text-gray-400 flex items-center gap-2 mt-1 font-mono uppercase tracking-wider">
                          <span>ID: {patient.patient_id}</span>
                          <span className="w-1 h-1 rounded-full bg-gray-600" />
                          <span>Ward: {patient.ward || "Unknown"}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 relative z-10">
                      <div className="p-3 rounded-xl bg-[#040d1a]/80 backdrop-blur-sm border border-amber-500/20 relative overflow-hidden group-hover:border-amber-500/40 transition-colors">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500/50" />
                        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1 pl-2">Trigger Source:</p>
                        <p className="text-sm font-medium text-amber-200 font-mono pl-2">{flagReason}</p>
                      </div>
                      
                      <div className="flex gap-2">
                        {pendingCount > 0 && (
                          <span className="text-[10px] font-bold text-purple-300 bg-purple-500/20 px-2 py-1 rounded border border-purple-500/30 flex items-center gap-1 uppercase tracking-wider">
                            <Clock className="w-3 h-3" /> {pendingCount} Pending Labs
                          </span>
                        )}
                        {patient.has_conflict && (
                          <span className="text-[10px] font-bold text-rose-300 bg-rose-500/20 px-2 py-1 rounded border border-rose-500/30 flex items-center gap-1 uppercase tracking-wider">
                            <Activity className="w-3 h-3" /> Desync
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Disclaimer */}
      <div className="text-center p-4">
        <p className="text-xs text-gray-500">
          Clinical Copilot is a decision-support prototype. Data is limited to operational datasets. <br/>
          <span className="text-amber-500/70 font-medium">Not an autonomous diagnostic tool. Clinician verification required.</span>
        </p>
      </div>
    </div>
  );
}
